import React, { useState, useEffect, useContext } from 'react';
import { Button, TextField, Grid } from '@material-ui/core';
import { io } from 'socket.io-client';
import { makeStyles } from '@material-ui/core/styles';
import { IoMdSend } from 'react-icons/io';
import { IoIosChatbubbles } from 'react-icons/io';
import { SocketContext } from '../Context';
import config from '../config';
import ChatMessage from '../Models/Types';

const socket = io(config.SOCKET_URL);

const useStyles = makeStyles((theme) => ({
  container: {
    width: '600px',
    margin: '35px 0',
    background: 'white',
    borderRadius: '5px',
    padding: 0,
    [theme.breakpoints.down('xs')]: {
      width: '80%',
    },
  },
  sendContainer: {
    display: 'flex',
    margin: '2px',
    padding: '5px',
    alignItems: 'center',
  },
  textArea: {
    margin: '5px',
    padding: '5px',
  },
  sendButton: {
    width: '30px',
    height: '100%',
    margin: '2px',
    padding: '2px',
    cursor: 'pointer',
  },
  msgContainer: {
    background: 'lightgrey',
    padding: '5px',
  },
  message: {
    padding: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sentMessage: {
    justifyContent: 'flex-end',
    textAlign: 'right',
    background: '#808080',
  },
  receivedMessage: {
    justifyContent: 'flex-start',
    textAlign: 'left',
    background: '#C0C0C0',
  },
  messageText: {
    maxWidth: '70%',
    wordWrap: 'break-word',
  },
  chatIcon: {
    position: 'fixed',
    bottom: '50px',
    right: '50px',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    backgroundColor: '#007bff',
    color: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    zIndex: 1000,
  },
  chatBox: {
    display: 'none',
    position: 'fixed',
    bottom: '100px',
    right: '80px',
    maxHeight: '400px',
    overflowY: 'auto',
    backgroundColor: 'white',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    borderRadius: '5px',
    zIndex: 1000,
  },
  chatBoxOpen: {
    display: 'block',
  },
}));

const MessagesDisplayComponent = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [message, setMessage] = useState('');
  const [isChatBoxOpen, setIsChatBoxOpen] = useState(false);
  const { sendMessage } = useContext(SocketContext);
  const { name } = useContext(SocketContext);
  const classes = useStyles();

  const send = (messageText: string) => {
    const message: ChatMessage = {
      Sender: name,
      Message: messageText,
      Time: new Date().toISOString(),
      Id: socket.id,
    };
    messageText && sendMessage(message);
  };

  useEffect(() => {
    socket.on('receiveMessage', (msg) => {
      const newMessage = {
        Sender: msg.Sender,
        Message: msg.Message,
        Time: msg.Time,
        Id: msg.Id,
      };

      setMessages((prevMessages) => [...prevMessages, newMessage]);
    });

    return () => {
      socket.off('receiveMessage');
    };
  }, []);

  const renderMessage = (msg: ChatMessage, index: number) => {
    const isSentByUser = msg.Id === socket.id;
    const isFirstMessage = index === 0;
    const isSameSenderAsPrevious =
      !isFirstMessage && messages[index - 1].Id === msg.Id;
    const borderRadiusStyle = isSameSenderAsPrevious
      ? { borderTopLeftRadius: '0px', borderTopRightRadius: '0px' }
      : { borderTopLeftRadius: '10px', borderTopRightRadius: '10px' };

    return (
      <div
        className={`${classes.message} ${
          isSentByUser ? classes.sentMessage : classes.receivedMessage
        }`}
        style={borderRadiusStyle}
        key={index}
      >
        {!isSentByUser && !isSameSenderAsPrevious && (
          <p className={classes.messageText}>
            <strong style={{ color: 'blue' }}>{msg.Sender}: </strong>
            {msg.Message}
          </p>
        )}
        {!isSentByUser && isSameSenderAsPrevious && (
          <p className={classes.messageText}>{msg.Message}</p>
        )}
        {isSentByUser && !isSameSenderAsPrevious && (
          <p className={classes.messageText} style={{ float: 'right' }}>
            {msg.Message}
            <strong style={{ color: 'blue' }}> :You </strong>
          </p>
        )}
        {isSentByUser && isSameSenderAsPrevious && (
          <p className={classes.messageText} style={{ float: 'right' }}>
            {msg.Message}
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className={classes.chatIcon}
        onClick={() => setIsChatBoxOpen(!isChatBoxOpen)}
      >
        <IoIosChatbubbles size={30} />
      </div>
      <div
        className={`${classes.chatBox} ${
          isChatBoxOpen ? classes.chatBoxOpen : ''
        }`}
      >
        <Grid className={classes.container}>
          <div className={classes.msgContainer}>
            {messages.map((msg, index) => renderMessage(msg, index))}
          </div>

          <div className={classes.sendContainer}>
            <TextField
              className={classes.textArea}
              label="Message"
              fullWidth
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  send(message);
                  setMessage(''); // Clear the input field after sending
                  e.preventDefault(); // Prevent the default Enter key behavior
                }
              }}
            />
            <IoMdSend
              className={classes.sendButton}
              onClick={() => {
                send(message);
                setMessage(''); // Clear the input field after sending
              }}
            />
          </div>
        </Grid>
      </div>
    </>
  );
};

export default MessagesDisplayComponent;